<template>
  <div class="product-group-header" :style="`top: ${stickyOffset}px`">
    <div class="product-group-header-left">
      <div class="title" @click="toggleCollapse">
        <div class="title-text-wrapper">
          <h2 class="product-group-name">{{ name }}</h2>
          <BackstageIcon :name="Icons.CIRCLE_LOADING" v-if="loadingProductPrices" />
          <span v-if="showPriceInfo" class="product-group-price">
            <BackstageTooltip :text="$t('matrix.wholesale_price')" v-if="showWholesalePriceCheck">
              {{ formatCurrency(currency, wholesalePrice) }}
            </BackstageTooltip>
            {{ showWholesalePriceCheck ? '&nbsp;/&nbsp;' : '' }}

            <BackstageTooltip :text="$t('matrix.retailer_price')">
              {{ formatCurrency(currency, retailerPrice) }}
            </BackstageTooltip>
          </span>
        </div>
        <span class="icon ico-chevron-down" :class="{ collapsed }"> </span>
      </div>
      <div class="remove-items icon ico-delete" v-on:click="removeAllQuantities"></div>
    </div>

    <div class="product-group-header-right">
      <div class="loading-wrapper" v-if="loadingProductPrices">
        <BackstageIcon :name="Icons.CIRCLE_LOADING" />
      </div>
      <div v-if="showPriceInfo && showWholesalePriceCheck" class="product-group-total-wsp">
        <span class="product-group-total-title">{{ $t('matrix.total_wholesale_price') }}</span>
        <span class="product-group-total-value">{{ formatCurrency(currency, wholeSalePriceTotal) }}</span>
      </div>
      <div v-if="showPriceInfo" class="product-group-total-rrp">
        <span class="product-group-total-title">{{ $t('matrix.total_retailer_price') }}</span>
        <span class="product-group-total-value">{{ formatCurrency(currency, retailerPriceTotal) }}</span>
      </div>
      <div class="product-group-total-quantities">
        <span class="product-group-total-title">{{ $t('matrix.total_quantities') }}</span>
        <span class="product-group-total-value">{{ total }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import { useFormat } from '@/composables/format';
import { useStickyOffset } from '@/composables/useStickyOffset';
import { Icons } from '@/models/icons.enum';
import { useProductPricesStore } from '@/stores/product-prices';
import type { ProductGroup } from '@/types/matrix.interface';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export interface Props {
  name: string;
  total?: number;
  collapsed: boolean;
  showWholesalePrice: boolean;
  productGroup: ProductGroup;
}

const props = defineProps<Props>();

const { formatCurrency } = useFormat();
const stickyOffset = useStickyOffset(['addEditNavBar', 'matrixRelatedOrdersSelector', 'quantitiesNavBar']);
const { productGroupItemPrice, loadingProductPrices, shouldLoadPricesOnProductMatrix } =
  storeToRefs(useProductPricesStore());

const productGroupPriceEntry = computed(() => {
  if (!props.productGroup?.fabrics) {
    return undefined;
  }

  for (const [fabricItemNumber] of props.productGroup.fabrics) {
    const itemPrice = productGroupItemPrice.value[fabricItemNumber];
    if (itemPrice && (itemPrice.retailer > 0 || (itemPrice.wholesale ?? 0) > 0)) {
      return itemPrice;
    }
  }

  const firstFabricItemNumber = props.productGroup.fabrics.entries().next().value?.[0];

  return firstFabricItemNumber ? productGroupItemPrice.value[firstFabricItemNumber] : undefined;
});

const currency = computed(() => {
  return productGroupPriceEntry.value?.currency;
});

const retailerPrice = computed(() => {
  return productGroupPriceEntry.value?.retailer || 0;
});

const wholesalePrice = computed(() => {
  return productGroupPriceEntry.value?.wholesale || 0;
});

const retailerPriceTotal = computed(() => {
  return retailerPrice.value * (props.total || 0);
});

const wholeSalePriceTotal = computed(() => {
  return wholesalePrice.value * (props.total || 0);
});

const showPriceInfo = computed(() => {
  return !loadingProductPrices.value && retailerPrice.value > 0 && shouldLoadPricesOnProductMatrix.value;
});

const showWholesalePriceCheck = computed(() => {
  return wholesalePrice.value > 0 && props.showWholesalePrice;
});

const emit = defineEmits<{
  (event: 'remove-all-quantities'): void;
  (event: 'toggle-collapse', collapsed: boolean): void;
}>();

function removeAllQuantities() {
  emit('remove-all-quantities');
}

function toggleCollapse() {
  emit('toggle-collapse', !props.collapsed);
}
</script>

<style lang="scss" scoped>
.product-group-header {
  padding: 12px 0 12px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--primary-bg-color);
  border-bottom: 1px solid var(--border-color-new);
  border-top: 1px solid var(--border-color-new);
  position: sticky;
  z-index: 2;
  height: initial;

  &:deep() {
    .circle-loading {
      width: 17px;
      height: 17px;
    }
  }

  .product-group-header-left {
    display: flex;
    align-items: center;
  }

  .product-group-header-right {
    display: flex;
  }

  .title-text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .product-group-name {
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    line-height: 150%;
  }

  .product-group-price {
    display: flex;
    font-size: 14px;
    color: var(--dark-gray-color);
  }

  .title {
    padding-left: 12px;
    display: flex;
    align-items: center;
    position: relative;
    flex: 1;
    cursor: pointer;
    background-color: var(--primary-bg-color);

    .ico-chevron-down {
      width: 17px;
      margin: 5px 5px 5px 9px;
      transform: rotate(180deg);
      transition: 0.2s ease-in;

      &.collapsed {
        transition: 0.2s ease-in;
        transform: rotate(0deg);
      }
    }
  }

  .remove-items {
    margin-left: 16px;
    cursor: pointer;
  }

  .product-group-total-rrp,
  .product-group-total-wsp,
  .product-group-total-quantities {
    min-width: 100px;
    padding: 0 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .product-group-total-title,
  .product-group-total-value {
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  .product-group-total-title {
    font-size: 12px;
  }

  .product-group-total-value {
    font-size: 14px;
  }

  @media (max-width: 1000px) {
    .title {
      padding: 0 10px;

      &:before {
        left: 2.5px;
      }
    }

    .product-group-total {
      width: 55px;
    }
  }

  @include breakpoint(medium) {
    .product-group-total {
      width: 35px;
    }
  }
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
}
</style>
